// Meld detection, minimum-deadwood partition, and layoffs for Gin Rummy.
// A meld is a set (3–4 of a rank) or a run (3+ consecutive cards of one suit, ace low).
// Every card belongs to at most one meld. Ties are broken deterministically by canonical
// ordering so all clients derive identical results.

import { CardId, deadwoodValue, handDeadwoodValue, rankIndex, sortCards, suitOf } from "./cards";

export interface MeldPartition {
  melds: CardId[][];
  deadwood: CardId[];
  deadwoodValue: number;
}

const meldKey = (meld: CardId[]) => sortCards(meld).join("");

/** Every candidate set and run present in the hand (including sub-runs and 3-of-4 sets). */
function candidateMelds(cards: CardId[]): CardId[][] {
  const melds: CardId[][] = [];

  // Sets: 3 or 4 of a rank. For a 4-set, also offer each 3-subset (a card may serve a run better).
  const byRank = new Map<string, CardId[]>();
  for (const card of cards) {
    const key = card[0];
    (byRank.get(key) ?? byRank.set(key, []).get(key)!).push(card);
  }
  for (const group of byRank.values()) {
    if (group.length >= 3) melds.push(group.slice());
    if (group.length === 4) {
      for (let skip = 0; skip < 4; skip++) melds.push(group.filter((_, i) => i !== skip));
    }
  }

  // Runs: consecutive ranks within a suit. Enumerate every sub-run of length >= 3.
  const bySuit = new Map<string, CardId[]>();
  for (const card of cards) {
    const key = suitOf(card);
    (bySuit.get(key) ?? bySuit.set(key, []).get(key)!).push(card);
  }
  for (const group of bySuit.values()) {
    const sorted = group.slice().sort((a, b) => rankIndex(a) - rankIndex(b));
    let block: CardId[] = [];
    const flush = () => {
      for (let start = 0; start < block.length; start++) {
        for (let end = start + 2; end < block.length; end++) {
          melds.push(block.slice(start, end + 1));
        }
      }
    };
    for (const card of sorted) {
      if (block.length === 0 || rankIndex(card) === rankIndex(block[block.length - 1]) + 1) {
        block.push(card);
      } else if (rankIndex(card) === rankIndex(block[block.length - 1])) {
        // duplicate rank in a suit is impossible in a legal deck; ignore defensively
      } else {
        flush();
        block = [card];
      }
    }
    flush();
  }

  // Canonical order makes the search deterministic; earliest maximum wins on ties.
  return melds.sort((a, b) => meldKey(a).localeCompare(meldKey(b)));
}

/** Partition a hand into melds that maximise melded value (minimise deadwood). */
export function bestMelds(cards: CardId[]): MeldPartition {
  const candidates = candidateMelds(cards);
  const index = new Map(cards.map((card, i) => [card, i]));
  const masks = candidates.map((meld) => meld.reduce((m, card) => m | (1 << index.get(card)!), 0));
  const values = candidates.map((meld) => handDeadwoodValue(meld));

  let best = { value: -1, chosen: [] as number[] };
  const dfs = (start: number, used: number, chosen: number[], value: number) => {
    if (value > best.value) best = { value, chosen: chosen.slice() };
    for (let i = start; i < candidates.length; i++) {
      if ((masks[i] & used) === 0) {
        chosen.push(i);
        dfs(i + 1, used | masks[i], chosen, value + values[i]);
        chosen.pop();
      }
    }
  };
  dfs(0, 0, [], 0);

  const melds = best.chosen.map((i) => sortCards(candidates[i]));
  const meldedMask = best.chosen.reduce((m, i) => m | masks[i], 0);
  const deadwood = cards.filter((card) => (meldedMask & (1 << index.get(card)!)) === 0);
  return { melds, deadwood, deadwoodValue: handDeadwoodValue(deadwood) };
}

/** Minimum deadwood value achievable for a hand. */
export const bestDeadwood = (cards: CardId[]): number => bestMelds(cards).deadwoodValue;

const isSet = (meld: CardId[]) => meld.length >= 1 && meld.every((c) => c[0] === meld[0][0]);

/**
 * Lay off a knock defender's deadwood onto the knocker's melds, greedily to a fixed point in
 * canonical card order. Returns the remaining deadwood after every legal layoff.
 */
export function layOff(deadwood: CardId[], knockerMelds: CardId[][]): { remaining: CardId[]; value: number } {
  const melds = knockerMelds.map((m) => sortCards(m));
  let remaining = sortCards(deadwood);
  let progressed = true;

  while (progressed) {
    progressed = false;
    for (const card of remaining) {
      const target = melds.find((meld) => {
        if (isSet(meld)) return meld[0][0] === card[0] && meld.length < 4;
        if (suitOf(meld[0]) !== suitOf(card)) return false;
        const lo = rankIndex(meld[0]);
        const hi = rankIndex(meld[meld.length - 1]);
        return rankIndex(card) === lo - 1 || rankIndex(card) === hi + 1;
      });
      if (target) {
        target.push(card);
        target.sort((a, b) => rankIndex(a) - rankIndex(b));
        remaining = remaining.filter((c) => c !== card);
        progressed = true;
        break;
      }
    }
  }

  return { remaining, value: remaining.reduce((sum, c) => sum + deadwoodValue(c), 0) };
}
